/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

interface PaymentRequiredFailureLike {
  code?: string | null;
  message: string;
  status: number;
}

export type PaymentRequiredCategory =
  | "subscription_inactive"
  | "subscription_read_only"
  | "budget_exceeded"
  | "usage_limit_exceeded"
  | "feature_limit_exceeded"
  | "unknown";

export interface PaymentRequiredDetails {
  category: PaymentRequiredCategory;
  code: string | null;
  status: number;
  message: string;
  title: string;
  description: string;
  shouldBlockApp: boolean;
}

export function mapPaymentRequiredFailure(
  failure: PaymentRequiredFailureLike,
): PaymentRequiredDetails {
  const code = failure.code ?? null;

  switch (code) {
    case "SUBSCRIPTION_INACTIVE":
      return {
        category: "subscription_inactive",
        code,
        status: failure.status,
        message: failure.message,
        title: "Subscription Inactive",
        description:
          "Your subscription is inactive. Update billing to continue using Contractor Co-Pilot.",
        shouldBlockApp: true,
      };
    case "SUBSCRIPTION_READ_ONLY":
      return {
        category: "subscription_read_only",
        code,
        status: failure.status,
        message: failure.message,
        title: "Read-only mode",
        description:
          "Your subscription has ended. You can still view and export data; reactivate billing to create new items.",
        shouldBlockApp: false,
      };
    case "BUDGET_EXCEEDED":
    case "AI_BUDGET_EXCEEDED":
      return {
        category: "budget_exceeded",
        code,
        status: failure.status,
        message: failure.message,
        title: "Monthly AI Budget Reached",
        description:
          "You've reached your AI budget for this billing period. Upgrade your plan to continue.",
        shouldBlockApp: false,
      };
    case "USAGE_LIMIT_EXCEEDED":
      return {
        category: "usage_limit_exceeded",
        code,
        status: failure.status,
        message: failure.message,
        title: "Usage Limit Reached",
        description:
          "You've reached a usage limit for this billing period. Upgrade your plan to continue.",
        shouldBlockApp: false,
      };
    case "FEATURE_LIMIT_EXCEEDED":
      return {
        category: "feature_limit_exceeded",
        code,
        status: failure.status,
        message: failure.message,
        title: "Feature Limit Reached",
        description:
          "You've reached the run limit for this feature in the current billing period.",
        shouldBlockApp: false,
      };
    default:
      return {
        category: "unknown",
        code,
        status: failure.status,
        message: failure.message,
        title: "Usage Limit Reached",
        description:
          failure.message ||
          "You've hit a billing or usage limit for your current plan.",
        shouldBlockApp: false,
      };
  }
}
